import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import {
  Settings,
  Search,
  Plus,
  Edit,
  Trash2,
  Percent,
  User,
  Users,
  Car,
  Building,
  Calendar,
  Phone,
  Mail,
  MapPin,
  FileText,
  Shield,
  Bell,
  Clock,
  ArrowRight,
  ArrowUpDown,
  Package,
  FolderOpen,
  AlertCircle,
  Beaker,
  Ruler
} from 'lucide-react';
import { mockSystemParametersData, getCategoryLabel, getLicenseTypeLabel, formatRUC, formatLicensePlate, getFrequencyLabel, formatTime } from './mocks/system-parameters.mock';
import {
  movementTypesService,
  presentationsService,
  productCategoriesService,
  positionsService,
  driversService,
  vehiclesService,
  transportCompaniesService,
  fiscalParametersService,
  ingredientCategoriesService,
  unitsService,
  MovementTypeAPI,
  PresentationAPI,
  ProductCategoryAPI,
  PositionAPI,
  DriverAPI,
  VehicleAPI,
  TransportCompanyAPI,
  FiscalParameterAPI,
  IngredientCategoryAPI,
  UnitAPI
} from '@/services/systemParametersService';
import { useNotifications } from '@/context/NotificationContext';
import { DriverModal } from './components/DriverModal';
import { DeleteConfirmationModal } from './components/DeleteConfirmationModal';
import { FiscalParameterModal } from './components/FiscalParameterModal';
import { VehicleModal } from './components/VehicleModal';
import { TransportCompanyModal } from './components/TransportCompanyModal';
import { ReminderModal } from './components/ReminderModal';
import { PositionModal } from './components/PositionModal';
import { MovementTypeModal } from './components/MovementTypeModal';
import { PresentationModal } from './components/PresentationModal';
import { ProductCategoryModal } from './components/ProductCategoryModal';
import { IngredientCategoryModal } from './components/IngredientCategoryModal';
import { UnitModal } from './components/UnitModal';
import { DriversTable } from './components/DriversTable';
import { VehiclesTable } from './components/VehiclesTable';
import { TransportCompaniesTable } from './components/TransportCompaniesTable';
import { RemindersTable } from './components/RemindersTable';
import { FiscalParametersTable } from './components/FiscalParametersTable';
import { PositionsTable } from './components/PositionsTable';
import { MovementTypesTable } from './components/MovementTypesTable';
import { PresentationsTable } from './components/PresentationsTable';
import { ProductCategoriesTable } from './components/ProductCategoriesTable';
import { IngredientCategoriesTable } from './components/IngredientCategoriesTable';
import { UnitsTable } from './components/UnitsTable';
import type {
  SystemParametersData,
  ParameterTab,
  ParameterFilters,
  FiscalParameter,
  Driver,
  Vehicle,
  TransportCompany,
  ModalState,
  DriverForm,
  FiscalParameterForm,
  VehicleForm,
  TransportCompanyForm,
  ReminderForm,
  Reminder,
  Position,
  MovementType,
  Presentation,
  ProductCategory,
  IngredientCategory as IngredientCategoryType,
  UnitOfMeasure,
  PositionForm,
  MovementTypeForm,
  PresentationForm,
  ProductCategoryForm,
  IngredientCategoryForm,
  UnitForm,
  AnySystemParameter
} from './types/system-parameters.types';

export const SystemParametersPage: React.FC = () => {
  const navigate = useNavigate();
  const { refreshReminders } = useNotifications();
  const [activeTab, setActiveTab] = useState<ParameterTab>('fiscal');
  const [parametersData, setParametersData] = useState<SystemParametersData>(mockSystemParametersData);
  const [loading, setLoading] = useState(false);

  // Loading states for API tabs
  const [loadingMovementTypes, setLoadingMovementTypes] = useState(false);
  const [loadingPresentations, setLoadingPresentations] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [loadingTransportCompanies, setLoadingTransportCompanies] = useState(false);
  const [loadingFiscalParameters, setLoadingFiscalParameters] = useState(false);
  const [loadingIngredientCategories, setLoadingIngredientCategories] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);

  // Track if data has been loaded from API
  const [dataLoaded, setDataLoaded] = useState({
    movement_types: false,
    presentations: false,
    product_categories: false,
    positions: false,
    drivers: false,
    vehicles: false,
    transport: false,
    fiscal: false,
    ingredientCategories: false,
    units: false
  });

  // Filtros
  const [filters, setFilters] = useState<ParameterFilters>({});
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states para cada categoría
  const [fiscalModal, setFiscalModal] = useState<ModalState>({ isOpen: false, mode: 'create' });
  const [driverModal, setDriverModal] = useState<ModalState>({ isOpen: false, mode: 'create' });
  const [vehicleModal, setVehicleModal] = useState<ModalState>({ isOpen: false, mode: 'create' });
  const [transportModal, setTransportModal] = useState<ModalState>({ isOpen: false, mode: 'create' });
  const [reminderModal, setReminderModal] = useState<ModalState>({ isOpen: false, mode: 'create' });
  const [positionModal, setPositionModal] = useState<ModalState>({ isOpen: false, mode: 'create' });
  const [movementTypeModal, setMovementTypeModal] = useState<ModalState>({ isOpen: false, mode: 'create' });
  const [presentationModal, setPresentationModal] = useState<ModalState>({ isOpen: false, mode: 'create' });
  const [productCategoryModal, setProductCategoryModal] = useState<ModalState>({ isOpen: false, mode: 'create' });
  const [ingredientCategoryModal, setIngredientCategoryModal] = useState<ModalState>({ isOpen: false, mode: 'create' });
  const [unitModal, setUnitModal] = useState<ModalState>({ isOpen: false, mode: 'create' });

  // Modal de confirmación de eliminación
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    item: AnySystemParameter | null;
  }>({
    isOpen: false,
    item: null
  });

  const [deletingItem, setDeletingItem] = useState(false);

  // Función de navegación para el descarte de pan
  const handleNavigateToMerchandiseMovements = () => {
    navigate('/merchandise-movements');
  };

  // Function to convert API MovementType to frontend format
  const convertMovementTypeAPIToFrontend = useCallback((apiData: MovementTypeAPI): MovementType => ({
    id: apiData.id,
    code: apiData.code,
    name: apiData.name,
    category: 'movement_types',
    type: 'text',
    value: apiData.code,
    description: apiData.description,
    isActive: apiData.isActive,
    createdAt: apiData.createdAt,
    updatedAt: apiData.updatedAt,
    updatedBy: 'sistema',
    additionalData: {
      code: apiData.code,
      name: apiData.name,
      description: apiData.description,
      direction: apiData.direction,
      requiresApproval: apiData.requiresApproval,
      isActive: apiData.isActive,
      category: apiData.category,
      orderIndex: apiData.orderIndex,
      companyId: apiData.companyId,
      createdAt: apiData.createdAt,
      updatedAt: apiData.updatedAt
    }
  }), []);

  // Function to convert API Presentation to frontend format
  const convertPresentationAPIToFrontend = useCallback((apiData: PresentationAPI): Presentation => ({
    id: `pres-${apiData.id}`,
    code: apiData.name.toUpperCase().replace(/\s+/g, '_'),
    name: apiData.name,
    category: 'presentations',
    type: 'text',
    value: apiData.name.toUpperCase(),
    description: apiData.description,
    isActive: apiData.isActive,
    createdAt: apiData.createdAt,
    updatedAt: apiData.updatedAt,
    updatedBy: 'sistema',
    additionalData: {
      name: apiData.name,
      description: apiData.description,
      isActive: apiData.isActive,
      createdAt: apiData.createdAt,
      updatedAt: apiData.updatedAt
    }
  }), []);

  // Function to convert API ProductCategory to frontend format
  const convertCategoryAPIToFrontend = useCallback((apiData: ProductCategoryAPI): ProductCategory => ({
    id: `cat-${apiData.id}`,
    code: apiData.code,
    name: apiData.name,
    category: 'product_categories',
    type: 'text',
    value: apiData.code,
    description: apiData.description,
    isActive: apiData.isActive,
    createdAt: apiData.createdAt,
    updatedAt: apiData.updatedAt,
    updatedBy: 'sistema',
    additionalData: {
      name: apiData.name,
      code: apiData.code,
      description: apiData.description,
      displayOrder: apiData.displayOrder,
      isActive: apiData.isActive,
      createdAt: apiData.createdAt,
      updatedAt: apiData.updatedAt
    }
  }), []);

  // Function to convert API Position to frontend format
  const convertPositionAPIToFrontend = useCallback((apiData: PositionAPI): Position => ({
    id: `pos-${apiData.id}`,
    code: apiData.code,
    name: apiData.name,
    category: 'positions',
    type: 'text',
    value: apiData.code,
    description: apiData.description,
    isActive: apiData.isActive,
    createdAt: apiData.createdAt,
    updatedAt: apiData.updatedAt,
    updatedBy: 'sistema',
    additionalData: {
      name: apiData.name,
      code: apiData.code,
      level: apiData.level,
      description: apiData.description,
      isActive: apiData.isActive,
      createdAt: apiData.createdAt,
      updatedAt: apiData.updatedAt
    }
  }), []);

  // Function to convert API Driver to frontend format
  const convertDriverAPIToFrontend = useCallback((apiData: DriverAPI): Driver => ({
    id: `driver-${apiData.id}`,
    code: `DRIVER_${String(apiData.id).padStart(3, '0')}`,
    name: apiData.fullName,
    category: 'drivers',
    type: 'text',
    value: apiData.isActive ? 'ACTIVO' : 'INACTIVO',
    description: `Conductor - ${apiData.licenseType}`,
    isActive: apiData.isActive,
    createdAt: apiData.createdAt,
    updatedAt: apiData.updatedAt,
    updatedBy: 'sistema',
    additionalData: {
      fullName: apiData.fullName,
      licenseNumber: apiData.licenseNumber,
      licenseType: apiData.licenseType,
      licenseExpiry: apiData.licenseExpiry,
      dni: apiData.dni,
      phone: apiData.phone,
      email: apiData.email,
      address: apiData.address,
      emergencyContact: apiData.emergencyContact ? { name: apiData.emergencyContact, phone: apiData.emergencyPhone } : undefined,
      isActive: apiData.isActive
    }
  }), []);

  // Function to convert API Vehicle to frontend format
  const convertVehicleAPIToFrontend = useCallback((apiData: VehicleAPI): Vehicle => ({
    id: `vehicle-${apiData.id}`,
    code: `VEHICLE_${String(apiData.id).padStart(3, '0')}`,
    name: `${apiData.brand} ${apiData.model}`,
    category: 'vehicles',
    type: 'text',
    value: apiData.plate,
    description: `Vehículo ${apiData.year} - ${apiData.color || 'N/A'}`,
    isActive: apiData.isActive,
    createdAt: apiData.createdAt,
    updatedAt: apiData.updatedAt,
    updatedBy: 'sistema',
    additionalData: {
      plate: apiData.plate,
      brand: apiData.brand,
      model: apiData.model,
      year: apiData.year,
      color: apiData.color,
      engineNumber: apiData.engineNumber,
      chassisNumber: apiData.chassisNumber,
      registrationExpiry: apiData.registrationExpiry,
      insuranceExpiry: apiData.insuranceExpiry,
      technicalReviewExpiry: apiData.technicalReviewExpiry,
      vehicleType: apiData.vehicleType,
      isActive: apiData.isActive
    }
  }), []);

  // Function to convert API TransportCompany to frontend format
  const convertTransportCompanyAPIToFrontend = useCallback((apiData: TransportCompanyAPI): TransportCompany => ({
    id: `transport-${apiData.id}`,
    code: `TRANSPORT_${String(apiData.id).padStart(3, '0')}`,
    name: apiData.companyName,
    category: 'transport',
    type: 'text',
    value: apiData.ruc,
    description: `Empresa de transporte - ${apiData.serviceTypes?.join(', ') || 'N/A'}`,
    isActive: apiData.isActive,
    createdAt: apiData.createdAt,
    updatedAt: apiData.updatedAt,
    updatedBy: 'sistema',
    additionalData: {
      companyName: apiData.companyName,
      ruc: apiData.ruc,
      address: apiData.address,
      phone: apiData.phone,
      email: apiData.email,
      contactPerson: apiData.contactPerson,
      contactPhone: apiData.contactPhone,
      mtcAuthorization: apiData.mtcAuthorization,
      authorizationExpiry: apiData.authorizationExpiry,
      serviceType: apiData.serviceTypes || [],
      isActive: apiData.isActive
    }
  }), []);

  // Function to convert API FiscalParameter to frontend format
  const convertFiscalParameterAPIToFrontend = useCallback((apiData: FiscalParameterAPI): FiscalParameter => ({
    id: `fiscal-${apiData.id}`,
    code: apiData.code,
    name: apiData.name,
    category: 'fiscal',
    type: 'percentage',
    value: String(apiData.value),
    description: apiData.description,
    isActive: apiData.isActive,
    createdAt: apiData.createdAt,
    updatedAt: apiData.updatedAt,
    updatedBy: 'sistema'
  }), []);

  // Function to convert API IngredientCategory to frontend format
  const convertIngredientCategoryAPIToFrontend = useCallback((apiData: IngredientCategoryAPI): IngredientCategoryType => ({
    id: `ingcat-${apiData.id}`,
    code: apiData.name,
    name: apiData.name,
    category: 'ingredient_categories',
    type: 'text',
    value: apiData.name,
    description: apiData.description || '',
    isActive: apiData.isActive,
    createdAt: apiData.createdAt,
    updatedAt: apiData.updatedAt,
    updatedBy: 'sistema',
    additionalData: {
      name: apiData.name,
      description: apiData.description || '',
      displayOrder: apiData.displayOrder,
      isActive: apiData.isActive,
      createdAt: apiData.createdAt,
      updatedAt: apiData.updatedAt
    }
  }), []);

  // Function to convert API Unit to frontend format
  const convertUnitAPIToFrontend = useCallback((apiData: UnitAPI): UnitOfMeasure => ({
    id: `unit-${apiData.id}`,
    code: apiData.name,
    name: apiData.displayName,
    category: 'units',
    type: 'text',
    value: apiData.name,
    description: apiData.displayName,
    isActive: apiData.isActive,
    createdAt: apiData.createdAt,
    updatedAt: apiData.updatedAt,
    updatedBy: 'sistema',
    additionalData: {
      name: apiData.name,
      displayName: apiData.displayName,
      category: apiData.category,
      symbol: apiData.symbol || '',
      sortOrder: apiData.sortOrder,
      isActive: apiData.isActive,
      createdAt: apiData.createdAt,
      updatedAt: apiData.updatedAt
    }
  }), []);

  // Load Movement Types from API
  const loadMovementTypes = useCallback(async () => {
    setLoadingMovementTypes(true);
    try {
      const apiData = await movementTypesService.getAll({ includeInactive: true });
      const frontendData = apiData.map(convertMovementTypeAPIToFrontend);

      setParametersData(prev => ({
        ...prev,
        movement_types: {
          items: frontendData,
          totalItems: frontendData.length
        }
      }));
      setDataLoaded(prev => ({ ...prev, movement_types: true }));
    } catch (error) {
      console.error('Error loading movement types:', error);
      toast.error('Error al cargar tipos de movimiento');
    } finally {
      setLoadingMovementTypes(false);
    }
  }, [convertMovementTypeAPIToFrontend]);

  // Load Presentations from API
  const loadPresentations = useCallback(async () => {
    setLoadingPresentations(true);
    try {
      const apiData = await presentationsService.getAll({ isActive: 'all' });
      const frontendData = apiData.map(convertPresentationAPIToFrontend);

      setParametersData(prev => ({
        ...prev,
        presentations: {
          items: frontendData,
          totalItems: frontendData.length
        }
      }));
      setDataLoaded(prev => ({ ...prev, presentations: true }));
    } catch (error) {
      console.error('Error loading presentations:', error);
      toast.error('Error al cargar presentaciones');
    } finally {
      setLoadingPresentations(false);
    }
  }, [convertPresentationAPIToFrontend]);

  // Load Product Categories from API
  const loadCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const apiData = await productCategoriesService.getAll({ isActive: 'all' });
      const frontendData = apiData.map(convertCategoryAPIToFrontend);

      setParametersData(prev => ({
        ...prev,
        product_categories: {
          items: frontendData,
          totalItems: frontendData.length
        }
      }));
      setDataLoaded(prev => ({ ...prev, product_categories: true }));
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Error al cargar categorías');
    } finally {
      setLoadingCategories(false);
    }
  }, [convertCategoryAPIToFrontend]);

  // Load Positions from API
  const loadPositions = useCallback(async () => {
    setLoadingPositions(true);
    try {
      const apiData = await positionsService.getAll({ isActive: 'all' });
      const frontendData = apiData.map(convertPositionAPIToFrontend);

      setParametersData(prev => ({
        ...prev,
        positions: {
          items: frontendData,
          totalItems: frontendData.length
        }
      }));
      setDataLoaded(prev => ({ ...prev, positions: true }));
    } catch (error) {
      console.error('Error loading positions:', error);
      toast.error('Error al cargar cargos');
    } finally {
      setLoadingPositions(false);
    }
  }, [convertPositionAPIToFrontend]);

  // Load Drivers from API
  const loadDrivers = useCallback(async () => {
    setLoadingDrivers(true);
    try {
      const apiData = await driversService.getAll({ isActive: 'all' });
      const frontendData = apiData.map(convertDriverAPIToFrontend);

      setParametersData(prev => ({
        ...prev,
        drivers: {
          items: frontendData,
          totalItems: frontendData.length
        }
      }));
      setDataLoaded(prev => ({ ...prev, drivers: true }));
    } catch (error) {
      console.error('Error loading drivers:', error);
      toast.error('Error al cargar conductores');
    } finally {
      setLoadingDrivers(false);
    }
  }, [convertDriverAPIToFrontend]);

  // Load Vehicles from API
  const loadVehicles = useCallback(async () => {
    setLoadingVehicles(true);
    try {
      const apiData = await vehiclesService.getAll({ isActive: 'all' });
      const frontendData = apiData.map(convertVehicleAPIToFrontend);

      setParametersData(prev => ({
        ...prev,
        vehicles: {
          items: frontendData,
          totalItems: frontendData.length
        }
      }));
      setDataLoaded(prev => ({ ...prev, vehicles: true }));
    } catch (error) {
      console.error('Error loading vehicles:', error);
      toast.error('Error al cargar vehículos');
    } finally {
      setLoadingVehicles(false);
    }
  }, [convertVehicleAPIToFrontend]);

  // Load Transport Companies from API
  const loadTransportCompanies = useCallback(async () => {
    setLoadingTransportCompanies(true);
    try {
      const apiData = await transportCompaniesService.getAll({ isActive: 'all' });
      const frontendData = apiData.map(convertTransportCompanyAPIToFrontend);

      setParametersData(prev => ({
        ...prev,
        transport: {
          items: frontendData,
          totalItems: frontendData.length
        }
      }));
      setDataLoaded(prev => ({ ...prev, transport: true }));
    } catch (error) {
      console.error('Error loading transport companies:', error);
      toast.error('Error al cargar empresas de transporte');
    } finally {
      setLoadingTransportCompanies(false);
    }
  }, [convertTransportCompanyAPIToFrontend]);

  // Load Fiscal Parameters from API
  const loadFiscalParameters = useCallback(async () => {
    setLoadingFiscalParameters(true);
    try {
      const apiData = await fiscalParametersService.getAll();
      const frontendData = apiData.map(convertFiscalParameterAPIToFrontend);

      setParametersData(prev => ({
        ...prev,
        fiscal: {
          items: frontendData,
          totalItems: frontendData.length
        }
      }));
      setDataLoaded(prev => ({ ...prev, fiscal: true }));
    } catch (error) {
      console.error('Error loading fiscal parameters:', error);
      toast.error('Error al cargar parámetros fiscales');
    } finally {
      setLoadingFiscalParameters(false);
    }
  }, [convertFiscalParameterAPIToFrontend]);

  // Load Ingredient Categories from API
  const loadIngredientCategories = useCallback(async () => {
    setLoadingIngredientCategories(true);
    try {
      const data = await ingredientCategoriesService.getAll({ isActive: 'all' });
      const converted = data.map(convertIngredientCategoryAPIToFrontend);
      setParametersData(prev => ({
        ...prev,
        ingredient_categories: { items: converted, totalItems: converted.length }
      }));
      setDataLoaded(prev => ({ ...prev, ingredientCategories: true }));
    } catch (error) {
      console.error('Error loading ingredient categories:', error);
      toast.error('Error al cargar categorías de insumos');
    } finally {
      setLoadingIngredientCategories(false);
    }
  }, [convertIngredientCategoryAPIToFrontend]);

  // Load Units from API
  const loadUnits = useCallback(async () => {
    setLoadingUnits(true);
    try {
      const data = await unitsService.getAll({ isActive: 'all' });
      const converted = data.map(convertUnitAPIToFrontend);
      setParametersData(prev => ({
        ...prev,
        units: { items: converted, totalItems: converted.length }
      }));
      setDataLoaded(prev => ({ ...prev, units: true }));
    } catch (error) {
      console.error('Error loading units:', error);
      toast.error('Error al cargar unidades de medida');
    } finally {
      setLoadingUnits(false);
    }
  }, [convertUnitAPIToFrontend]);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'fiscal' && !dataLoaded.fiscal) {
      loadFiscalParameters();
    } else if (activeTab === 'movement_types' && !dataLoaded.movement_types) {
      loadMovementTypes();
    } else if (activeTab === 'presentations' && !dataLoaded.presentations) {
      loadPresentations();
    } else if (activeTab === 'product_categories' && !dataLoaded.product_categories) {
      loadCategories();
    } else if (activeTab === 'positions' && !dataLoaded.positions) {
      loadPositions();
    } else if (activeTab === 'drivers' && !dataLoaded.drivers) {
      loadDrivers();
    } else if (activeTab === 'vehicles' && !dataLoaded.vehicles) {
      loadVehicles();
    } else if (activeTab === 'transport' && !dataLoaded.transport) {
      loadTransportCompanies();
    } else if (activeTab === 'ingredient_categories' && !dataLoaded.ingredientCategories) {
      loadIngredientCategories();
    } else if (activeTab === 'units' && !dataLoaded.units) {
      loadUnits();
    }
  }, [activeTab, dataLoaded, loadFiscalParameters, loadMovementTypes, loadPresentations, loadCategories, loadPositions, loadDrivers, loadVehicles, loadTransportCompanies, loadIngredientCategories, loadUnits]);

  // Load fiscal parameters on initial mount (since fiscal is the default tab)
  useEffect(() => {
    if (!dataLoaded.fiscal) {
      loadFiscalParameters();
    }
  }, []);

  // Datos filtrados por categoría
  const filteredFiscalParameters = useMemo(() => {
    return parametersData.fiscal.items.filter(item => {
      const matchesSearch = !searchTerm ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.code.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [parametersData.fiscal.items, searchTerm]);

  const filteredDrivers = useMemo(() => {
    return parametersData.drivers.items.filter(item => {
      const matchesSearch = !searchTerm ||
        item.additionalData.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.additionalData.licenseNumber.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [parametersData.drivers.items, searchTerm]);

  const filteredVehicles = useMemo(() => {
    return parametersData.vehicles.items.filter(item => {
      const matchesSearch = !searchTerm ||
        item.additionalData.plate.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.additionalData.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.additionalData.model.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [parametersData.vehicles.items, searchTerm]);

  const filteredTransportCompanies = useMemo(() => {
    return parametersData.transport.items.filter(item => {
      const matchesSearch = !searchTerm ||
        item.additionalData.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.additionalData.ruc.includes(searchTerm);
      return matchesSearch;
    });
  }, [parametersData.transport.items, searchTerm]);

  const filteredReminders = useMemo(() => {
    return parametersData.reminders.items.filter(item => {
      const matchesSearch = !searchTerm ||
        item.additionalData.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.additionalData.description.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [parametersData.reminders.items, searchTerm]);

  const filteredPositions = useMemo(() => {
    return parametersData.positions.items.filter(item => {
      const matchesSearch = !searchTerm ||
        item.additionalData.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.additionalData.code.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [parametersData.positions.items, searchTerm]);

  const filteredMovementTypes = useMemo(() => {
    return parametersData.movement_types.items.filter(item => {
      const matchesSearch = !searchTerm ||
        item.additionalData.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.additionalData.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.additionalData.category.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [parametersData.movement_types.items, searchTerm]);

  const filteredPresentations = useMemo(() => {
    return parametersData.presentations.items.filter(item => {
      const matchesSearch = !searchTerm ||
        item.additionalData.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.additionalData.description.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [parametersData.presentations.items, searchTerm]);

  const filteredProductCategories = useMemo(() => {
    return parametersData.product_categories.items.filter(item => {
      const matchesSearch = !searchTerm ||
        item.additionalData.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.additionalData.code.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [parametersData.product_categories.items, searchTerm]);

  const filteredIngredientCategories = useMemo(() => {
    return parametersData.ingredient_categories.items.filter(item => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return item.name.toLowerCase().includes(search) || (item.description || '').toLowerCase().includes(search);
    });
  }, [parametersData.ingredient_categories.items, searchTerm]);

  const filteredUnits = useMemo(() => {
    return parametersData.units.items.filter(item => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return item.name.toLowerCase().includes(search) || item.additionalData.name.toLowerCase().includes(search) || item.additionalData.symbol.toLowerCase().includes(search);
    });
  }, [parametersData.units.items, searchTerm]);

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  // Funciones CRUD para Parámetros Fiscales
  const handleSaveFiscalParameter = async (formData: FiscalParameterForm, isEdit = false, itemId?: string) => {
    setLoading(true);
    try {
      if (isEdit && itemId) {
        // Extract numeric ID from 'fiscal-X' format
        const numericId = parseInt(itemId.replace('fiscal-', ''));
        // Actualizar parámetro fiscal existente via API
        await fiscalParametersService.update(numericId, {
          name: formData.name,
          code: formData.code,
          value: Number(formData.value),
          description: formData.description
        });
        toast.success('Parámetro fiscal actualizado exitosamente');
      } else {
        // Crear nuevo parámetro fiscal via API
        await fiscalParametersService.create({
          name: formData.name,
          code: formData.code,
          value: Number(formData.value),
          description: formData.description
        });
        toast.success('Parámetro fiscal creado exitosamente');
      }
      // Reload data from API
      await loadFiscalParameters();
    } catch (error) {
      console.error('Error saving fiscal parameter:', error);
      toast.error(error instanceof Error ? error.message : 'Error al guardar parámetro fiscal');
    } finally {
      setLoading(false);
    }
  };

  // Funciones CRUD para Conductores
  const handleSaveDriver = async (formData: DriverForm, isEdit = false, itemId?: string) => {
    setLoading(true);
    try {
      if (isEdit && itemId) {
        // Extract numeric ID from 'driver-X' format
        const numericId = parseInt(itemId.replace('driver-', ''));
        // Actualizar conductor existente via API
        await driversService.update(numericId, {
          fullName: formData.fullName,
          licenseNumber: formData.licenseNumber,
          licenseType: formData.licenseType,
          licenseExpiry: formData.licenseExpiry,
          dni: formData.dni,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          emergencyContact: formData.emergencyContact?.name,
          emergencyPhone: formData.emergencyContact?.phone
        });
        toast.success('Conductor actualizado exitosamente');
      } else {
        // Crear nuevo conductor via API
        await driversService.create({
          fullName: formData.fullName,
          licenseNumber: formData.licenseNumber,
          licenseType: formData.licenseType,
          licenseExpiry: formData.licenseExpiry,
          dni: formData.dni,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          emergencyContact: formData.emergencyContact?.name,
          emergencyPhone: formData.emergencyContact?.phone
        });
        toast.success('Conductor creado exitosamente');
      }
      // Reload data from API
      await loadDrivers();
    } catch (error) {
      console.error('Error saving driver:', error);
      toast.error(error instanceof Error ? error.message : 'Error al guardar conductor');
    } finally {
      setLoading(false);
    }
  };

  // Funciones CRUD para Vehículos
  const handleSaveVehicle = async (formData: VehicleForm, isEdit = false, itemId?: string) => {
    setLoading(true);
    try {
      if (isEdit && itemId) {
        // Extract numeric ID from 'vehicle-X' format
        const numericId = parseInt(itemId.replace('vehicle-', ''));
        // Actualizar vehículo existente via API
        await vehiclesService.update(numericId, {
          plate: formData.plate,
          brand: formData.brand,
          model: formData.model,
          year: formData.year,
          color: formData.color,
          engineNumber: formData.engineNumber,
          chassisNumber: formData.chassisNumber,
          registrationExpiry: formData.registrationExpiry,
          insuranceExpiry: formData.insuranceExpiry,
          technicalReviewExpiry: formData.technicalReviewExpiry,
          vehicleType: formData.vehicleType
        });
        toast.success('Vehículo actualizado exitosamente');
      } else {
        // Crear nuevo vehículo via API
        await vehiclesService.create({
          plate: formData.plate,
          brand: formData.brand,
          model: formData.model,
          year: formData.year,
          color: formData.color,
          engineNumber: formData.engineNumber,
          chassisNumber: formData.chassisNumber,
          registrationExpiry: formData.registrationExpiry,
          insuranceExpiry: formData.insuranceExpiry,
          technicalReviewExpiry: formData.technicalReviewExpiry,
          vehicleType: formData.vehicleType
        });
        toast.success('Vehículo creado exitosamente');
      }
      // Reload data from API
      await loadVehicles();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error(error instanceof Error ? error.message : 'Error al guardar vehículo');
    } finally {
      setLoading(false);
    }
  };

  // Funciones CRUD para Empresas de Transporte
  const handleSaveTransportCompany = async (formData: TransportCompanyForm, isEdit = false, itemId?: string) => {
    setLoading(true);
    try {
      if (isEdit && itemId) {
        // Extract numeric ID from 'transport-X' format
        const numericId = parseInt(itemId.replace('transport-', ''));
        // Actualizar empresa de transporte existente via API
        await transportCompaniesService.update(numericId, {
          companyName: formData.companyName,
          ruc: formData.ruc,
          address: formData.address,
          phone: formData.phone,
          email: formData.email,
          contactPerson: formData.contactPerson,
          contactPhone: formData.contactPhone,
          mtcAuthorization: formData.mtcAuthorization,
          authorizationExpiry: formData.authorizationExpiry,
          serviceTypes: formData.serviceType
        });
        toast.success('Empresa de transporte actualizada exitosamente');
      } else {
        // Crear nueva empresa de transporte via API
        await transportCompaniesService.create({
          companyName: formData.companyName,
          ruc: formData.ruc,
          address: formData.address,
          phone: formData.phone,
          email: formData.email,
          contactPerson: formData.contactPerson,
          contactPhone: formData.contactPhone,
          mtcAuthorization: formData.mtcAuthorization,
          authorizationExpiry: formData.authorizationExpiry,
          serviceTypes: formData.serviceType
        });
        toast.success('Empresa de transporte creada exitosamente');
      }
      // Reload data from API
      await loadTransportCompanies();
    } catch (error) {
      console.error('Error saving transport company:', error);
      toast.error(error instanceof Error ? error.message : 'Error al guardar empresa de transporte');
    } finally {
      setLoading(false);
    }
  };

  // Funciones CRUD para Recordatorios
  const handleSaveReminder = (formData: ReminderForm, isEdit = false, itemId?: string) => {
    if (isEdit && itemId) {
      // Actualizar recordatorio existente
      setParametersData(prev => ({
        ...prev,
        reminders: {
          ...prev.reminders,
          items: prev.reminders.items.map(reminder =>
            reminder.id === itemId
              ? {
                  ...reminder,
                  name: formData.action,
                  value: formData.frequency,
                  description: `Recordatorio ${formData.frequency} - ${formData.action}`,
                  updatedAt: new Date().toISOString(),
                  updatedBy: 'usuario_actual',
                  additionalData: {
                    ...formData,
                    lastTriggered: reminder.additionalData.lastTriggered,
                    nextTrigger: calculateNextTrigger(formData.frequency, formData.time)
                  }
                }
              : reminder
          )
        }
      }));
    } else {
      // Crear nuevo recordatorio
      const newReminder: Reminder = {
        id: `reminder-${Date.now()}`,
        code: `REMINDER_${String(parametersData.reminders.totalItems + 1).padStart(3, '0')}`,
        name: formData.action,
        category: 'reminders',
        type: 'text',
        value: formData.frequency,
        description: `Recordatorio ${formData.frequency} - ${formData.action}`,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        updatedBy: 'usuario_actual',
        additionalData: {
          ...formData,
          nextTrigger: calculateNextTrigger(formData.frequency, formData.time)
        }
      };

      setParametersData(prev => ({
        ...prev,
        reminders: {
          items: [...prev.reminders.items, newReminder],
          totalItems: prev.reminders.totalItems + 1
        }
      }));
    }

    // Actualizar las notificaciones después de guardar
    refreshReminders();
  };

  // Función auxiliar para calcular próxima ejecución
  const calculateNextTrigger = (frequency: string, time: string): string => {
    const now = new Date();
    const [hours, minutes] = time.split(':').map(Number);

    let nextDate = new Date(now);
    nextDate.setHours(hours, minutes, 0, 0);

    // Si la hora ya pasó hoy, programar para el próximo período
    if (nextDate <= now) {
      switch (frequency) {
        case 'daily':
          nextDate.setDate(nextDate.getDate() + 1);
          break;
        case 'weekly':
          nextDate.setDate(nextDate.getDate() + 7);
          break;
        case 'monthly':
          nextDate.setMonth(nextDate.getMonth() + 1);
          break;
        case 'once':
          nextDate.setDate(nextDate.getDate() + 1);
          break;
      }
    }

    return nextDate.toISOString();
  };

  // Funciones CRUD para Posiciones
  const handleSavePosition = async (formData: PositionForm, isEdit = false, itemId?: string) => {
    setLoading(true);
    try {
      if (isEdit && itemId) {
        // Extract numeric ID from 'pos-X' format
        const numericId = parseInt(itemId.replace('pos-', ''));
        // Actualizar posición existente via API
        await positionsService.update(numericId, {
          name: formData.name,
          code: formData.code,
          level: formData.level,
          description: formData.description
        });
        toast.success('Cargo actualizado exitosamente');
      } else {
        // Crear nueva posición via API
        await positionsService.create({
          name: formData.name,
          code: formData.code,
          level: formData.level,
          description: formData.description
        });
        toast.success('Cargo creado exitosamente');
      }
      // Reload data from API
      await loadPositions();
    } catch (error) {
      console.error('Error saving position:', error);
      toast.error(error instanceof Error ? error.message : 'Error al guardar cargo');
    } finally {
      setLoading(false);
    }
  };

  // Funciones CRUD para Tipos de Movimiento
  const handleSaveMovementType = async (formData: MovementTypeForm, isEdit = false, itemId?: string) => {
    setLoading(true);
    try {
      if (isEdit && itemId) {
        // Actualizar tipo de movimiento existente via API
        await movementTypesService.update(itemId, {
          code: formData.code,
          name: formData.name,
          description: formData.description,
          direction: formData.direction,
          requiresApproval: formData.requiresApproval,
          category: formData.category,
          orderIndex: formData.orderIndex
        });
        toast.success('Tipo de movimiento actualizado exitosamente');
      } else {
        // Crear nuevo tipo de movimiento via API
        await movementTypesService.create({
          code: formData.code,
          name: formData.name,
          description: formData.description,
          direction: formData.direction,
          requiresApproval: formData.requiresApproval,
          category: formData.category,
          orderIndex: formData.orderIndex
        });
        toast.success('Tipo de movimiento creado exitosamente');
      }
      // Reload data from API
      await loadMovementTypes();
    } catch (error) {
      console.error('Error saving movement type:', error);
      toast.error(error instanceof Error ? error.message : 'Error al guardar tipo de movimiento');
    } finally {
      setLoading(false);
    }
  };

  // Funciones CRUD para Presentaciones
  const handleSavePresentation = async (formData: PresentationForm, isEdit = false, itemId?: string) => {
    setLoading(true);
    try {
      if (isEdit && itemId) {
        // Extract numeric ID from 'pres-X' format
        const numericId = parseInt(itemId.replace('pres-', ''));
        // Actualizar presentación existente via API
        await presentationsService.update(numericId, {
          name: formData.name,
          description: formData.description
        });
        toast.success('Presentación actualizada exitosamente');
      } else {
        // Crear nueva presentación via API
        await presentationsService.create({
          name: formData.name,
          description: formData.description
        });
        toast.success('Presentación creada exitosamente');
      }
      // Reload data from API
      await loadPresentations();
    } catch (error) {
      console.error('Error saving presentation:', error);
      toast.error(error instanceof Error ? error.message : 'Error al guardar presentación');
    } finally {
      setLoading(false);
    }
  };

  // Funciones CRUD para Categorías de Productos
  const handleSaveProductCategory = async (formData: ProductCategoryForm, isEdit = false, itemId?: string) => {
    setLoading(true);
    try {
      if (isEdit && itemId) {
        // Extract numeric ID from 'cat-X' format
        const numericId = parseInt(itemId.replace('cat-', ''));
        // Actualizar categoría existente via API
        await productCategoriesService.update(numericId, {
          code: formData.code,
          name: formData.name,
          description: formData.description,
          displayOrder: formData.displayOrder
        });
        toast.success('Categoría actualizada exitosamente');
      } else {
        // Crear nueva categoría via API
        await productCategoriesService.create({
          code: formData.code,
          name: formData.name,
          description: formData.description,
          displayOrder: formData.displayOrder
        });
        toast.success('Categoría creada exitosamente');
      }
      // Reload data from API
      await loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(error instanceof Error ? error.message : 'Error al guardar categoría');
    } finally {
      setLoading(false);
    }
  };

  // Funciones CRUD para Categorías de Insumos
  const handleSaveIngredientCategory = async (formData: IngredientCategoryForm, isEdit = false, itemId?: string) => {
    setLoading(true);
    try {
      if (isEdit && itemId) {
        const numericId = parseInt(itemId.replace('ingcat-', ''));
        await ingredientCategoriesService.update(numericId, {
          name: formData.name,
          description: formData.description,
          displayOrder: formData.displayOrder
        });
        toast.success('Categoría de insumo actualizada exitosamente');
      } else {
        await ingredientCategoriesService.create({
          name: formData.name,
          description: formData.description,
          displayOrder: formData.displayOrder
        });
        toast.success('Categoría de insumo creada exitosamente');
      }
      await loadIngredientCategories();
    } catch (error) {
      console.error('Error saving ingredient category:', error);
      toast.error(error instanceof Error ? error.message : 'Error al guardar categoría de insumo');
    } finally {
      setLoading(false);
    }
  };

  // Funciones CRUD para Unidades de Medida
  const handleSaveUnit = async (formData: UnitForm, isEdit = false, itemId?: string) => {
    setLoading(true);
    try {
      if (isEdit && itemId) {
        const numericId = parseInt(itemId.replace('unit-', ''));
        await unitsService.update(numericId, {
          name: formData.name,
          displayName: formData.displayName,
          category: formData.category,
          symbol: formData.symbol,
          sortOrder: formData.sortOrder
        });
        toast.success('Unidad de medida actualizada exitosamente');
      } else {
        await unitsService.create({
          name: formData.name,
          displayName: formData.displayName,
          category: formData.category,
          symbol: formData.symbol,
          sortOrder: formData.sortOrder
        });
        toast.success('Unidad de medida creada exitosamente');
      }
      await loadUnits();
    } catch (error) {
      console.error('Error saving unit:', error);
      toast.error(error instanceof Error ? error.message : 'Error al guardar unidad de medida');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (item: AnySystemParameter) => {
    setDeletingItem(true);

    try {
      // Handle API toggle (activate/deactivate) for connected tabs
      if (item.category === 'movement_types') {
        const isCurrentlyActive = (item as MovementType).additionalData?.isActive;
        const newStatus = !isCurrentlyActive;
        await movementTypesService.toggleStatus(item.id, newStatus);
        toast.success(`Tipo de movimiento ${newStatus ? 'reactivado' : 'desactivado'} exitosamente`);
        await loadMovementTypes();
      } else if (item.category === 'presentations') {
        const isCurrentlyActive = (item as Presentation).additionalData?.isActive;
        const numericId = parseInt(item.id.replace('pres-', ''));
        await presentationsService.toggle(numericId);
        toast.success(`Presentación ${!isCurrentlyActive ? 'reactivada' : 'desactivada'} exitosamente`);
        await loadPresentations();
      } else if (item.category === 'product_categories') {
        const isCurrentlyActive = (item as ProductCategory).additionalData?.isActive;
        const numericId = parseInt(item.id.replace('cat-', ''));
        await productCategoriesService.toggle(numericId);
        toast.success(`Categoría ${!isCurrentlyActive ? 'reactivada' : 'desactivada'} exitosamente`);
        await loadCategories();
      } else if (item.category === 'positions') {
        const isCurrentlyActive = (item as Position).additionalData?.isActive;
        const numericId = parseInt(item.id.replace('pos-', ''));
        await positionsService.toggle(numericId);
        toast.success(`Cargo ${!isCurrentlyActive ? 'reactivado' : 'desactivado'} exitosamente`);
        await loadPositions();
      } else if (item.category === 'drivers') {
        const isCurrentlyActive = (item as Driver).additionalData?.isActive;
        const numericId = parseInt(item.id.replace('driver-', ''));
        await driversService.toggle(numericId);
        toast.success(`Conductor ${!isCurrentlyActive ? 'reactivado' : 'desactivado'} exitosamente`);
        await loadDrivers();
      } else if (item.category === 'vehicles') {
        const isCurrentlyActive = (item as Vehicle).additionalData?.isActive;
        const numericId = parseInt(item.id.replace('vehicle-', ''));
        await vehiclesService.toggle(numericId);
        toast.success(`Vehículo ${!isCurrentlyActive ? 'reactivado' : 'desactivado'} exitosamente`);
        await loadVehicles();
      } else if (item.category === 'transport') {
        const isCurrentlyActive = (item as TransportCompany).additionalData?.isActive;
        const numericId = parseInt(item.id.replace('transport-', ''));
        await transportCompaniesService.toggle(numericId);
        toast.success(`Empresa de transporte ${!isCurrentlyActive ? 'reactivada' : 'desactivada'} exitosamente`);
        await loadTransportCompanies();
      } else if (item.category === 'fiscal') {
        const isCurrentlyActive = (item as FiscalParameter).isActive;
        const numericId = parseInt(item.id.replace('fiscal-', ''));
        await fiscalParametersService.toggle(numericId);
        toast.success(`Parámetro fiscal ${!isCurrentlyActive ? 'reactivado' : 'desactivado'} exitosamente`);
        await loadFiscalParameters();
      } else if (item.category === 'ingredient_categories') {
        const numericId = parseInt(item.id.replace('ingcat-', ''));
        await ingredientCategoriesService.toggle(numericId);
        toast.success('Estado de categoría de insumo actualizado');
        await loadIngredientCategories();
      } else if (item.category === 'units') {
        const numericId = parseInt(item.id.replace('unit-', ''));
        await unitsService.toggle(numericId);
        toast.success('Estado de unidad de medida actualizado');
        await loadUnits();
      } else {
        // For non-API tabs (reminders), use local state manipulation
        await new Promise(resolve => setTimeout(resolve, 800));

        setParametersData(prev => {
          switch (item.category) {
            case 'reminders':
              return {
                ...prev,
                reminders: {
                  items: prev.reminders.items.filter(r => r.id !== item.id),
                  totalItems: prev.reminders.totalItems - 1
                }
              };
            default:
              return prev;
          }
        });

        // Actualizar notificaciones si se eliminó un recordatorio
        if (item.category === 'reminders') {
          refreshReminders();
        }
      }

      // Cerrar modal
      setDeleteModal({ isOpen: false, item: null });
    } catch (error) {
      console.error('Error al procesar elemento:', error);
      const isToggleCategory = ['movement_types', 'presentations', 'product_categories', 'positions', 'drivers', 'vehicles', 'transport', 'fiscal', 'ingredient_categories', 'units'].includes(item.category);
      toast.error(error instanceof Error ? error.message : `Error al ${isToggleCategory ? 'cambiar estado del' : 'eliminar'} elemento`);
    } finally {
      setDeletingItem(false);
    }
  };

  const openDeleteModal = (item: AnySystemParameter) => {
    setDeleteModal({ isOpen: true, item });
  };

  const closeDeleteModal = () => {
    if (!deletingItem) {
      setDeleteModal({ isOpen: false, item: null });
    }
  };

  // Función para determinar si una fecha está próxima a vencer (30 días)
  const isExpiringSoon = (dateString: string): boolean => {
    const expiryDate = new Date(dateString);
    const today = new Date();
    const diffTime = expiryDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  // Función para determinar si una fecha está vencida
  const isExpired = (dateString: string): boolean => {
    const expiryDate = new Date(dateString);
    const today = new Date();
    return expiryDate < today;
  };

  // Tab de Parámetros Fiscales
  const renderFiscalTab = () => (
    <div className="space-y-6">
      {/* Header con búsqueda y botón agregar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-500" />
            <Input
              placeholder="Buscar parámetros fiscales..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
          </div>
          <Button variant="outline" onClick={clearFilters} size="sm">
            Limpiar
          </Button>
        </div>
        <Button
          onClick={() => setFiscalModal({ isOpen: true, mode: 'create' })}
          className="bg-macartur-primary"
          disabled={loadingFiscalParameters}
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar Parámetro
        </Button>
      </div>

      {/* Tabla de Parámetros Fiscales */}
      <Card>
        <CardHeader>
          <CardTitle>Parámetros Fiscales</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingFiscalParameters ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-macartur-primary"></div>
              <span className="ml-3 text-gray-500">Cargando parámetros fiscales...</span>
            </div>
          ) : (
            <FiscalParametersTable
              parameters={filteredFiscalParameters}
              onEdit={(parameter) => setFiscalModal({ isOpen: true, mode: 'edit', item: parameter })}
              onDelete={openDeleteModal}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Tab de Conductores
  const renderDriversTab = () => (
    <div className="space-y-6">
      {/* Header con búsqueda y botón agregar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-500" />
            <Input
              placeholder="Buscar conductores..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
          </div>
          <Button variant="outline" onClick={clearFilters} size="sm">
            Limpiar
          </Button>
        </div>
        <Button
          onClick={() => setDriverModal({ isOpen: true, mode: 'create' })}
          className="bg-macartur-primary"
          disabled={loadingDrivers}
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar Conductor
        </Button>
      </div>

      {/* Tabla de Conductores */}
      <Card>
        <CardHeader>
          <CardTitle>Conductores</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingDrivers ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-macartur-primary"></div>
              <span className="ml-3 text-gray-500">Cargando conductores...</span>
            </div>
          ) : (
            <DriversTable
              drivers={filteredDrivers}
              onEdit={(driver) => setDriverModal({ isOpen: true, mode: 'edit', item: driver })}
              onDelete={openDeleteModal}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Tab de Vehículos
  const renderVehiclesTab = () => (
    <div className="space-y-6">
      {/* Header con búsqueda y botón agregar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-500" />
            <Input
              placeholder="Buscar vehículos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
          </div>
          <Button variant="outline" onClick={clearFilters} size="sm">
            Limpiar
          </Button>
        </div>
        <Button
          onClick={() => setVehicleModal({ isOpen: true, mode: 'create' })}
          className="bg-macartur-primary"
          disabled={loadingVehicles}
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar Vehículo
        </Button>
      </div>

      {/* Tabla de Vehículos */}
      <Card>
        <CardHeader>
          <CardTitle>Vehículos</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingVehicles ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-macartur-primary"></div>
              <span className="ml-3 text-gray-500">Cargando vehículos...</span>
            </div>
          ) : (
            <VehiclesTable
              vehicles={filteredVehicles}
              onEdit={(vehicle) => setVehicleModal({ isOpen: true, mode: 'edit', item: vehicle })}
              onDelete={openDeleteModal}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Tab de Empresas de Transporte
  const renderTransportTab = () => (
    <div className="space-y-6">
      {/* Header con búsqueda y botón agregar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-500" />
            <Input
              placeholder="Buscar empresas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
          </div>
          <Button variant="outline" onClick={clearFilters} size="sm">
            Limpiar
          </Button>
        </div>
        <Button
          onClick={() => setTransportModal({ isOpen: true, mode: 'create' })}
          className="bg-macartur-primary"
          disabled={loadingTransportCompanies}
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar Empresa
        </Button>
      </div>

      {/* Tabla de Empresas de Transporte */}
      <Card>
        <CardHeader>
          <CardTitle>Empresas de Transporte</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingTransportCompanies ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-macartur-primary"></div>
              <span className="ml-3 text-gray-500">Cargando empresas de transporte...</span>
            </div>
          ) : (
            <TransportCompaniesTable
              companies={filteredTransportCompanies}
              onEdit={(company) => setTransportModal({ isOpen: true, mode: 'edit', item: company })}
              onDelete={openDeleteModal}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );


  // Tab de Cargos
  const renderPositionsTab = () => (
    <div className="space-y-6">
      {/* Header con búsqueda y botón agregar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-500" />
            <Input
              placeholder="Buscar cargos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
          </div>
          <Button variant="outline" onClick={clearFilters} size="sm">
            Limpiar
          </Button>
        </div>
        <Button
          onClick={() => setPositionModal({ isOpen: true, mode: 'create' })}
          className="bg-macartur-primary"
          disabled={loadingPositions}
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar Cargo
        </Button>
      </div>

      {/* Tabla de Cargos */}
      <Card>
        <CardHeader>
          <CardTitle>Cargos</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingPositions ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-macartur-primary"></div>
              <span className="ml-3 text-gray-500">Cargando cargos...</span>
            </div>
          ) : (
            <PositionsTable
              positions={filteredPositions}
              onEdit={(position) => setPositionModal({ isOpen: true, mode: 'edit', item: position })}
              onDelete={openDeleteModal}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Tab de Tipos de Movimiento
  const renderMovementTypesTab = () => (
    <div className="space-y-6">
      {/* Header con búsqueda y botón agregar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-500" />
            <Input
              placeholder="Buscar tipos de movimiento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
          </div>
          <Button variant="outline" onClick={clearFilters} size="sm">
            Limpiar
          </Button>
        </div>
        <Button
          onClick={() => setMovementTypeModal({ isOpen: true, mode: 'create' })}
          className="bg-macartur-primary"
          disabled={loadingMovementTypes}
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar Tipo de Movimiento
        </Button>
      </div>

      {/* Tabla de Tipos de Movimiento */}
      <Card>
        <CardHeader>
          <CardTitle>Tipos de Movimiento</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingMovementTypes ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-macartur-primary"></div>
              <span className="ml-3 text-gray-500">Cargando tipos de movimiento...</span>
            </div>
          ) : (
            <MovementTypesTable
              movementTypes={filteredMovementTypes}
              onEdit={(movementType) => setMovementTypeModal({ isOpen: true, mode: 'edit', item: movementType })}
              onDelete={openDeleteModal}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Tab de Presentaciones
  const renderPresentationsTab = () => (
    <div className="space-y-6">
      {/* Header con búsqueda y botón agregar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-500" />
            <Input
              placeholder="Buscar presentaciones..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
          </div>
          <Button variant="outline" onClick={clearFilters} size="sm">
            Limpiar
          </Button>
        </div>
        <Button
          onClick={() => setPresentationModal({ isOpen: true, mode: 'create' })}
          className="bg-macartur-primary"
          disabled={loadingPresentations}
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar Presentación
        </Button>
      </div>

      {/* Tabla de Presentaciones */}
      <Card>
        <CardHeader>
          <CardTitle>Presentaciones</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingPresentations ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-macartur-primary"></div>
              <span className="ml-3 text-gray-500">Cargando presentaciones...</span>
            </div>
          ) : (
            <PresentationsTable
              presentations={filteredPresentations}
              onEdit={(presentation) => setPresentationModal({ isOpen: true, mode: 'edit', item: presentation })}
              onDelete={openDeleteModal}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Tab de Categorías de Producto
  const renderProductCategoriesTab = () => (
    <div className="space-y-6">
      {/* Header con búsqueda y botón agregar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-500" />
            <Input
              placeholder="Buscar categorías..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
          </div>
          <Button variant="outline" onClick={clearFilters} size="sm">
            Limpiar
          </Button>
        </div>
        <Button
          onClick={() => setProductCategoryModal({ isOpen: true, mode: 'create' })}
          className="bg-macartur-primary"
          disabled={loadingCategories}
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar Categoría
        </Button>
      </div>

      {/* Tabla de Categorías de Producto */}
      <Card>
        <CardHeader>
          <CardTitle>Categorías de Producto</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingCategories ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-macartur-primary"></div>
              <span className="ml-3 text-gray-500">Cargando categorías...</span>
            </div>
          ) : (
            <ProductCategoriesTable
              categories={filteredProductCategories}
              onEdit={(category) => setProductCategoryModal({ isOpen: true, mode: 'edit', item: category })}
              onDelete={openDeleteModal}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Tab de Categorías de Insumos
  const renderIngredientCategoriesTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Categorías de Insumos</CardTitle>
          <Button onClick={() => setIngredientCategoryModal({ isOpen: true, mode: 'create' })} className="bg-macartur-primary">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Categoría
          </Button>
        </CardHeader>
        <CardContent>
          {loadingIngredientCategories ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-macartur-primary"></div></div>
          ) : (
            <IngredientCategoriesTable
              categories={filteredIngredientCategories}
              onEdit={(category) => setIngredientCategoryModal({ isOpen: true, mode: 'edit', item: category })}
              onDelete={openDeleteModal}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Tab de Unidades de Medida
  const renderUnitsTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Unidades de Medida</CardTitle>
          <Button onClick={() => setUnitModal({ isOpen: true, mode: 'create' })} className="bg-macartur-primary">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Unidad
          </Button>
        </CardHeader>
        <CardContent>
          {loadingUnits ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-macartur-primary"></div></div>
          ) : (
            <UnitsTable
              units={filteredUnits}
              onEdit={(unit) => setUnitModal({ isOpen: true, mode: 'edit', item: unit })}
              onDelete={openDeleteModal}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <>
      <div className="p-6 space-y-6">
      {/* Navigation Tabs - Single Row */}
      <div className="border-b pb-4">
        <div className="flex flex-wrap gap-2">
          <Button
            variant={activeTab === 'fiscal' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => {
              setActiveTab('fiscal');
              setSearchTerm('');
            }}
            className={activeTab === 'fiscal' ? 'bg-macartur-primary' : ''}
          >
            <Percent className="w-4 h-4 mr-2" />
            Fiscales
          </Button>
          <Button
            variant={activeTab === 'drivers' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => {
              setActiveTab('drivers');
              setSearchTerm('');
            }}
            className={activeTab === 'drivers' ? 'bg-macartur-primary' : ''}
          >
            <User className="w-4 h-4 mr-2" />
            Conductores
          </Button>
          <Button
            variant={activeTab === 'vehicles' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => {
              setActiveTab('vehicles');
              setSearchTerm('');
            }}
            className={activeTab === 'vehicles' ? 'bg-macartur-primary' : ''}
          >
            <Car className="w-4 h-4 mr-2" />
            Vehículos
          </Button>
          <Button
            variant={activeTab === 'transport' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => {
              setActiveTab('transport');
              setSearchTerm('');
            }}
            className={activeTab === 'transport' ? 'bg-macartur-primary' : ''}
          >
            <Building className="w-4 h-4 mr-2" />
            Transporte
          </Button>
          <Button
            variant={activeTab === 'positions' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => {
              setActiveTab('positions');
              setSearchTerm('');
            }}
            className={activeTab === 'positions' ? 'bg-macartur-primary' : ''}
          >
            <Users className="w-4 h-4 mr-2" />
            Cargos
          </Button>
          <Button
            variant={activeTab === 'movement_types' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => {
              setActiveTab('movement_types');
              setSearchTerm('');
            }}
            className={activeTab === 'movement_types' ? 'bg-macartur-primary' : ''}
          >
            <ArrowUpDown className="w-4 h-4 mr-2" />
            Movimientos
          </Button>
          <Button
            variant={activeTab === 'presentations' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => {
              setActiveTab('presentations');
              setSearchTerm('');
            }}
            className={activeTab === 'presentations' ? 'bg-macartur-primary' : ''}
          >
            <Package className="w-4 h-4 mr-2" />
            Presentaciones
          </Button>
          <Button
            variant={activeTab === 'product_categories' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => {
              setActiveTab('product_categories');
              setSearchTerm('');
            }}
            className={activeTab === 'product_categories' ? 'bg-macartur-primary' : ''}
          >
            <FolderOpen className="w-4 h-4 mr-2" />
            Categorías
          </Button>
          <Button
            variant={activeTab === 'ingredient_categories' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => {
              setActiveTab('ingredient_categories');
              setSearchTerm('');
            }}
            className={activeTab === 'ingredient_categories' ? 'bg-macartur-primary' : ''}
          >
            <Beaker className="w-4 h-4 mr-2" />
            Cat. Insumos
          </Button>
          <Button
            variant={activeTab === 'units' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => {
              setActiveTab('units');
              setSearchTerm('');
            }}
            className={activeTab === 'units' ? 'bg-macartur-primary' : ''}
          >
            <Ruler className="w-4 h-4 mr-2" />
            Unidades
          </Button>
        </div>
      </div>

        {/* Content */}
        {activeTab === 'fiscal' && renderFiscalTab()}
        {activeTab === 'drivers' && renderDriversTab()}
        {activeTab === 'vehicles' && renderVehiclesTab()}
        {activeTab === 'transport' && renderTransportTab()}
        {activeTab === 'positions' && renderPositionsTab()}
        {activeTab === 'movement_types' && renderMovementTypesTab()}
        {activeTab === 'presentations' && renderPresentationsTab()}
        {activeTab === 'product_categories' && renderProductCategoriesTab()}
        {activeTab === 'ingredient_categories' && renderIngredientCategoriesTab()}
        {activeTab === 'units' && renderUnitsTab()}
      </div>

      {/* Modales */}
      <FiscalParameterModal
        modalState={fiscalModal}
        onClose={() => setFiscalModal({ isOpen: false, mode: 'create' })}
        onSave={handleSaveFiscalParameter}
      />

      <DriverModal
        modalState={driverModal}
        onClose={() => setDriverModal({ isOpen: false, mode: 'create' })}
        onSave={handleSaveDriver}
      />

      <VehicleModal
        modalState={vehicleModal}
        onClose={() => setVehicleModal({ isOpen: false, mode: 'create' })}
        onSave={handleSaveVehicle}
      />

      <TransportCompanyModal
        modalState={transportModal}
        onClose={() => setTransportModal({ isOpen: false, mode: 'create' })}
        onSave={handleSaveTransportCompany}
      />

      <ReminderModal
        modalState={reminderModal}
        onClose={() => setReminderModal({ isOpen: false, mode: 'create' })}
        onSave={handleSaveReminder}
      />

      <PositionModal
        modalState={positionModal}
        onClose={() => setPositionModal({ isOpen: false, mode: 'create' })}
        onSave={handleSavePosition}
      />

      <MovementTypeModal
        modalState={movementTypeModal}
        onClose={() => setMovementTypeModal({ isOpen: false, mode: 'create' })}
        onSave={handleSaveMovementType}
      />

      <PresentationModal
        modalState={presentationModal}
        onClose={() => setPresentationModal({ isOpen: false, mode: 'create' })}
        onSave={handleSavePresentation}
      />

      <ProductCategoryModal
        modalState={productCategoryModal}
        onClose={() => setProductCategoryModal({ isOpen: false, mode: 'create' })}
        onSave={handleSaveProductCategory}
        totalCategories={parametersData.product_categories.items.length}
      />

      <IngredientCategoryModal
        modalState={ingredientCategoryModal}
        onClose={() => setIngredientCategoryModal({ isOpen: false, mode: 'create' })}
        onSave={handleSaveIngredientCategory}
        totalCategories={parametersData.ingredient_categories.items.length}
      />

      <UnitModal
        modalState={unitModal}
        onClose={() => setUnitModal({ isOpen: false, mode: 'create' })}
        onSave={handleSaveUnit}
      />

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        item={deleteModal.item}
        onClose={closeDeleteModal}
        onConfirm={() => deleteModal.item && handleDeleteItem(deleteModal.item)}
        loading={deletingItem}
      />
    </>
  );
};
